import * as React from "react";
import {
  Skeleton,
} from "@/components/ui/skeleton";
import {
  cn,
} from "@/lib/utils";

/**
 * Skeletons mirror the geometry of the real layout — 48px table rows, 36px inputs,
 * 24px card padding — so resolving never shifts the page.
 * Render them only after ~200ms of pending; never for optimistic updates.
 */

export function TableSkeleton({
  rows = 6,
  showCheckbox = true,
  numericColumns = 2,
}: {
  rows?: number;
  showCheckbox?: boolean;
  numericColumns?: number;
}) {
  return (
    <div aria-busy className="divide-y">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex h-12 items-center gap-4 px-6">
          {showCheckbox && <Skeleton className="size-4 rounded-[4px]" />}
          <div className="flex flex-1 flex-col gap-1.5">
            <Skeleton className="h-2.5 w-[--w]" style={{ ["--w" as string]: `${140 + ((i * 37) % 80)}px` }} />
            <Skeleton className="h-1.5 w-24 opacity-60" />
          </div>
          <Skeleton className="h-5 w-20 rounded-full" />
          {Array.from({ length: numericColumns }).map((_, n) => (
            <Skeleton key={n} className="h-2.5 w-12 opacity-60" />
          ))}
        </div>
      ))}
    </div>
  );
}

export function StatTilesSkeleton({ count = 3 }: { count?: number }) {
  return (
    <div aria-busy className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="flex flex-col gap-2.5 rounded-[var(--radius)] border bg-card p-6">
          <Skeleton className="h-2 w-20 opacity-60" />
          <Skeleton className="h-6 w-14" />
          <Skeleton className="h-1.5 w-24 opacity-60" />
        </div>
      ))}
    </div>
  );
}

export function CardGridSkeleton({
  count = 4,
  columns = 2,
}: {
  count?: number;
  columns?: 2 | 3;
}) {
  return (
    <div
      aria-busy
      className={cn(
        "grid grid-cols-1 gap-6",
        columns === 3 ? "md:grid-cols-3" : "md:grid-cols-2"
      )}
    >
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="flex flex-col gap-3 rounded-[var(--radius)] border bg-card p-6">
          <div className="flex items-center gap-3">
            <Skeleton className="size-9 rounded-[10px]" />
            <div className="flex flex-1 flex-col gap-1.5">
              <Skeleton className="h-2.5 w-28" />
              <Skeleton className="h-1.5 w-16 opacity-60" />
            </div>
          </div>
          <Skeleton className="h-1.5 w-full opacity-60" />
          <Skeleton className="h-1.5 w-2/3 opacity-60" />
        </div>
      ))}
    </div>
  );
}

export function DetailSkeleton({ metaRows = 4 }: { metaRows?: number }) {
  return (
    <div aria-busy className="flex flex-col gap-6 p-6">
      <div className="flex items-center gap-3">
        <Skeleton className="size-11 rounded-xl" />
        <div className="flex flex-1 flex-col gap-2">
          <Skeleton className="h-3 w-2/3" />
          <Skeleton className="h-2 w-1/3 opacity-60" />
        </div>
      </div>
      <Skeleton className="h-px w-full rounded-none opacity-60" />
      {Array.from({ length: metaRows }).map((_, i) => (
        <div key={i} className="flex items-center justify-between gap-4">
          <Skeleton className="h-2 w-16 opacity-60" />
          <Skeleton className="h-2 w-24" />
        </div>
      ))}
      <Skeleton className="h-32 w-full rounded-[var(--radius)]" />
    </div>
  );
}

export function FormSkeleton({ fields = 5 }: { fields?: number }) {
  return (
    <div aria-busy className="flex flex-col gap-4 p-6">
      {Array.from({ length: fields }).map((_, i) => (
        <div key={i} className="flex flex-col gap-2">
          <Skeleton className="h-2 w-20 opacity-60" />
          <Skeleton className="h-9 w-full rounded-[var(--radius)]" />
        </div>
      ))}
      <div className="flex gap-2 pt-2">
        <Skeleton className="h-9 w-32 rounded-[var(--radius)]" />
        <Skeleton className="h-9 w-20 rounded-[var(--radius)] opacity-60" />
      </div>
    </div>
  );
}
